import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Field, Textarea, Modal } from "@/components/kit";

const box = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px] focus:outline-none focus:border-maroon/50";

export default function MemberSubmit() {
  const [access, setAccess] = useState(null);
  const [open, setOpen] = useState(null); // "reimb" | "inkind"
  const [f, setF] = useState({ amount: "", value: "", description: "", receipt: "" });

  useEffect(() => { api.get("/reimbursements/my-access").then((r) => setAccess(r.data)).catch(() => setAccess({})); }, []);
  if (!access || (!access.reimbursements && !access.in_kind)) return null;

  const onReceipt = (e) => { const file = e.target.files?.[0]; if (!file) return; const rd = new FileReader(); rd.onload = () => setF((p) => ({ ...p, receipt: rd.result })); rd.readAsDataURL(file); };
  const close = () => { setOpen(null); setF({ amount: "", value: "", description: "", receipt: "" }); };
  const submit = async () => {
    if (!f.description.trim()) return toast.error("Add a short description.");
    try {
      if (open === "reimb") {
        const cents = Math.round(parseFloat(f.amount || "0") * 100);
        if (!cents) return toast.error("Enter an amount.");
        await api.post("/reimbursements", { amount_cents: cents, description: f.description, surface: "self", receipt: f.receipt });
      } else {
        const cents = Math.round(parseFloat(f.value || "0") * 100);
        await api.post("/in-kind", { estimated_value_cents: cents, description: f.description, surface: "self", receipt: f.receipt });
      }
      toast.success("Submitted — thank you! Staff will review it."); close();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
  };

  return (
    <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="member-submit">
      <h3 className="font-zilla text-lg font-semibold mb-1">Reimbursements & contributions</h3>
      <p className="text-[13px] text-ink/60 mb-3">Spent your own money on the association, or gave goods or time? Send it in for the board to review.</p>
      <div className="flex flex-wrap gap-2">
        {access.reimbursements && <Button data-testid="member-submit-reimb" onClick={() => setOpen("reimb")}><Icon name="Receipt" className="w-4 h-4" /> Submit an expense</Button>}
        {access.in_kind && <Button variant="olive" data-testid="member-submit-inkind" onClick={() => setOpen("inkind")}><Icon name="Gift" className="w-4 h-4" /> Log an in-kind contribution</Button>}
      </div>

      <Modal open={open === "reimb"} onClose={close} title="Submit an expense"
        footer={<><Button variant="secondary" onClick={close}>Cancel</Button><Button data-testid="member-reimb-save" onClick={submit}>Submit for review</Button></>}>
        <div className="space-y-3">
          <Field label="Amount you spent ($)"><input type="number" data-testid="member-reimb-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={box} /></Field>
          <Field label="What was it for?"><Textarea data-testid="member-reimb-desc" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="e.g. Mulch and gloves for the pollinator bed" /></Field>
          <div className="border border-dashed border-ink/25 rounded-lg p-3 text-center">
            {f.receipt && <img src={f.receipt} alt="" className="mx-auto max-h-24 rounded mb-2" />}
            <label className="inline-flex items-center gap-2 bg-ink text-cream rounded-lg px-3 py-2 text-[13px] font-semibold cursor-pointer"><Icon name="Upload" className="w-4 h-4" /> Attach receipt<input type="file" accept="image/*" data-testid="member-reimb-receipt" className="hidden" onChange={onReceipt} /></label>
          </div>
        </div>
      </Modal>

      <Modal open={open === "inkind"} onClose={close} title="Log an in-kind contribution"
        footer={<><Button variant="secondary" onClick={close}>Cancel</Button><Button variant="olive" data-testid="member-inkind-save" onClick={submit}>Submit for review</Button></>}>
        <div className="space-y-3">
          <Field label="What did you contribute?"><Textarea data-testid="member-inkind-desc" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="e.g. Donated 20 folding chairs for the annual meeting" /></Field>
          <Field label="Estimated value ($, optional)"><input type="number" data-testid="member-inkind-value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className={box} /></Field>
          <div className="border border-dashed border-ink/25 rounded-lg p-3 text-center">
            {f.receipt && <img src={f.receipt} alt="" className="mx-auto max-h-24 rounded mb-2" />}
            <label className="inline-flex items-center gap-2 bg-ink text-cream rounded-lg px-3 py-2 text-[13px] font-semibold cursor-pointer"><Icon name="Upload" className="w-4 h-4" /> Attach a photo<input type="file" accept="image/*" data-testid="member-inkind-receipt" className="hidden" onChange={onReceipt} /></label>
          </div>
        </div>
      </Modal>
    </div>
  );
}
